/**
 * The ch03 feature manifest — season launch and initial allocation.
 *
 * Eight routes. The first five are the app «Asignación inicial» (bata-platform#939, `docs/adr/9082`,
 * `docs/adr/5001`), the only pages a planner is offered: the rail draws them inside «Próximas ampliaciones»,
 * usable — the articles to allocate, the simulation, the runs, the lines and why, the approvals. The other
 * three are the chapter's first pages and are disabled ONE BY ONE (not the chapter): they are not drawn beside
 * the app, and go once it covers them. Their split is reqs/03's own:
 *
 * | Route | reqs/03 | The question it answers |
 * |---|---|---|
 * | `plan` | §1, §3.5 | What are we shipping, to which door, in which size, in which week — and do the carve-outs reconcile |
 * | `size-runs` | §3.2 C5, §8 | Does every served door open the season with a complete core run — gate D3 |
 * | `withholding` | §3.4, reqs/07 §3.6 | How much do we hold for the chase, and what is the bridge that justifies it |
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than
 * census failures later.
 *
 * The drill targets follow reqs/07 §3.1's object chain rather than convenience: a
 * launch line drills to the door's size run, and the size run drills onward to the
 * in-season position, which is the same unit answered later in the season. That is the
 * chain reqs/08 §3 rank 4 merges into one engine, made navigable.
 */

import { defineFeature } from '../manifest';
import { LAUNCH_GUIDES } from './guides';

import { shipmentListExport, sizeRunExport, withholdingExport } from './exports';
import {
  ARTICLES_EXPORT_ID, RUN_EXPORT_IDS, approvalsExport, articlesExport, linesExport, runsExport,
} from './initial/files';

export default defineFeature({
  slug: 'launch',
  titleKey: 'launch.title',
  navLabelKey: 'launch.navLabel',
  icon: 'split',  // brand guide p.14
  railGroup: 'future',  // bata-ui#132 — a chapter's section; the rail's layout does not read it (`docs/adr/9082`)
  localeNamespace: 'launch',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: LAUNCH_GUIDES,
  // No chapter-level `disabled`: it would grey the whole chapter, the app's page with it
  // (`notOffered()`). Each dormant page says so itself (`docs/adr/9082`).
  navigation: [
    // The app «Asignación inicial» — `RAIL_APPS`, `placement: 'future'`: one entry per page (R6).
    { labelKey: 'launch.nav.initial', to: '/launch/initial', order: 29 },
    { labelKey: 'launch.nav.initialSimulate', to: '/launch/initial/simulate', order: 29.1 },
    { labelKey: 'launch.nav.initialRuns', to: '/launch/initial/runs', order: 29.2 },
    { labelKey: 'launch.nav.initialLines', to: '/launch/initial/lines', order: 29.3 },
    { labelKey: 'launch.nav.initialApprovals', to: '/launch/initial/approvals', order: 29.4 },
    { labelKey: 'launch.nav.plan', to: '/launch/plan', order: 30, disabled: true },
    { labelKey: 'launch.nav.sizeRuns', to: '/launch/size-runs', order: 31, disabled: true },
    { labelKey: 'launch.nav.withholding', to: '/launch/withholding', order: 32, disabled: true },
  ],
  routes: [
    {
      // The articles the DC holds that are new, each with why it can or cannot be allocated today; «Nueva ejecución».
      path: 'initial',
      titleKey: 'launch.initial.title',
      urlParams: ['market', 'view'],
      component: () => import('./screens/InitialAllocationScreen'),
      surfaces: ['table', 'form'],
      exports: { id: ARTICLES_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: articlesExport },
      drill: { to: '/launch/initial/runs', grain: 'sku' },
    },
    {
      // Change the parameters a simulation admits, run it, compare it with the live run. Decides nothing.
      path: 'initial/simulate',
      titleKey: 'launch.initial.simulate.title',
      urlParams: ['market', 'initialRunId'],
      component: () => import('./screens/InitialSimulateScreen'),
      surfaces: ['form'],
    },
    {
      // One run's report: figures, articles by verdict, the hold-back comparison of the opened article, the cells.
      path: 'initial/runs',
      titleKey: 'launch.initial.runs.title',
      urlParams: ['market', 'initialRunId', 'state', 'article', 'view'],
      component: () => import('./screens/InitialRunsScreen'),
      surfaces: ['table'],
      exports: { id: RUN_EXPORT_IDS.runs, formats: ['csv', 'xlsx'], resolve: runsExport },
      drill: { to: '/launch/initial/lines', grain: 'sku', alsoTo: ['/launch/initial/approvals'] },
    },
    {
      // Template P: the run's lines and the evidence behind each. Decides nothing; links to the queue.
      path: 'initial/lines',
      titleKey: 'launch.initial.lines.title',
      urlParams: ['market', 'initialRunId', 'row', 'f.article', 'store', 'f.reason'],
      component: () => import('./screens/InitialLinesScreen'),
      surfaces: ['table'],
      exports: { id: RUN_EXPORT_IDS.lines, formats: ['csv', 'xlsx'], resolve: linesExport },
      drill: { to: '/launch/initial/approvals', grain: 'sku' },
    },
    {
      // Template A: the run's undecided lines, approved or rejected one by one or an article at a time; nothing is written back.
      path: 'initial/approvals',
      titleKey: 'launch.initial.approvals.title',
      urlParams: ['market', 'initialRunId', 'row', 'f.article', 'store', 'f.reason'],
      component: () => import('./screens/InitialApprovalsScreen'),
      surfaces: ['table', 'form'],
      exports: { id: RUN_EXPORT_IDS.approvals, formats: ['csv', 'xlsx'], resolve: approvalsExport },
      drill: {
        terminal: true,
        reason: 'An article first push is the unit the platform records a decision on; its lines are read on the lines page, which the row links to.',
      },
    },
    {
      path: 'plan',
      titleKey: 'launch.plan.title',
      urlParams: ['option'],
      component: () => import('./screens/LaunchPlanScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'launch.plan.grid',
        formats: ['csv', 'xlsx'],
        resolve: shipmentListExport,
      },
      drill: { to: '/launch/size-runs', grain: 'store', alsoTo: ['/launch/withholding'] },
    },
    {
      path: 'size-runs',
      titleKey: 'launch.sizeRun.title',
      urlParams: ['option'],
      component: () => import('./screens/SizeRunScreen'),
      // `tile` only since F8 (2026-09-27): the fixture-backed heatmap and curve were removed.
      surfaces: ['tile'],
      exports: {
        id: 'launch.sizeRun.report',
        formats: ['csv', 'xlsx'],
        resolve: sizeRunExport,
      },
      drill: { to: '/launch/plan', grain: 'sku' },
    },
    {
      path: 'withholding',
      titleKey: 'launch.withholding.title',
      urlParams: ['option'],
      component: () => import('./screens/WithholdingScreen'),
      // `tile` only since F8 (2026-09-27): the fixture-backed sweep, bridge and
      // recommendation were removed; the realised hold-back is what remains.
      surfaces: ['tile'],
      exports: {
        id: 'launch.withholding.realised',
        formats: ['csv', 'xlsx'],
        resolve: withholdingExport,
      },
      drill: { to: '/launch/plan', grain: 'category' },
    },
  ],
});
